// @ts-nocheck
/**
 * useDialerSessionHandlers — Phase 4 etape 7b.
 *
 * Hooks et handlers de la session de dialing extraits de DialerContext.jsx :
 * - useDialingMutation : mutation React Query pour lancer un batch d'appels
 * - useDialerSessionHandlers : agrege la mutation + handleStartDialing/
 *   StopAll/HangupAndContinue/HangupSingleCall/SkipBatch
 *
 * Toutes les dépendances métier sont passées explicitement aux args du
 * hook — pas de globals, pas de closure cachée sur le scope du provider.
 *
 * Code IDENTIQUE à la version originale dans DialerContext.jsx — aucun
 * changement de comportement, juste un déplacement de fichier.
 */

import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/api/api-client';
import * as TwilioCalls from '@/services/dialer/TwilioCallService';
import { canCall, dialIntent, sortByDialIntent, ARCHIVED_OUTCOMES, CALLBACK_OUTCOMES, isInterruptedCall } from '@/services/dialer/dialRules';
import { toast } from 'sonner';
import { stopUpdatesFor, isAudibleCall } from '@/services/dialer/stopUpdates';
import { hasMeetingBooked } from '@/utils/callUtils';
import { TELEPHONY_REQUIRED_EVENT } from '@/services/dialer/telephonyIssue';
import { disconnectDeviceCalls } from '@/lib/twilioDevice';

export function useDialingMutation({
  // Refs
  pendingDialsRef,
  stopRequestedRef,
  lastStopAtRef,
  connectedContactRef,
  dialedPhonesThisSessionRef,
  // States + setters
  parallelCallsCount,
  prospectQueue,
  filteredProspectQueue,
  latestCallByPhone,
  isProspectAtCallLimit,
  selectedSource,
  selectedCallerId,
  getNextCallerId = null,
  dialScopeIds = null,
  callerData,
  deviceIdentity,
  deviceReady,
  smartQueueEnabled,
  voicemailMode,
  voicemailAudioUrl,
  aiCoachEnabledPowerMode,
  completeTaskOnDial,
  allCalls,
  maxCallAttempts,
  maxCallAttemptsPeriod,
  dialerRunState,
  setDialerRunState,
  setPowerMode,
  setDialedPhonesThisSession,
  powerHourActive,
  setPowerHourCalls,
  queryClient,
}) {
  const { t } = useTranslation(['dialer']);
  return useMutation({
    onMutate: (input) => {
      const requested = Math.max(1, Math.min(
        parallelCallsCount,
        Number.isFinite(input?.count) ? input.count : parallelCallsCount,
      ));
      pendingDialsRef.current += requested;
      console.log('[DIAL] onMutate: pendingDialsRef +', requested, '→', pendingDialsRef.current);
    },
    mutationFn: async (input) => {
      // GARDE DURE : sans Twilio Device registered, le TwiML <Dial><Client>
      // cote serveur cherche une identite qui n'existe pas → Twilio coupe
      // l'appel a ~1s. Incident caller@revcall.com (2026-05-19) : 20 appels
      // rates de suite faute de Device. Bloquer ici evite de creer les
      // ParallelCall puis d'appeler Twilio inutilement.
      if (!deviceReady) {
        const err = new Error('Téléphonie non connectée — vérifiez votre microphone et rechargez la page.');
        err.code = 'DEVICE_NOT_READY';
        throw err;
      }
      const requestedCount = Math.max(1, Math.min(
        parallelCallsCount,
        Number.isFinite(input?.count) ? input.count : parallelCallsCount,
      ));
      console.log('[DIAL] mutation start', {
        prospectQueueSize: prospectQueue.length,
        parallelCallsCount,
        requestedCount,
        selectedSource,
        selectedCallerId,
        callerData_organisationId: callerData?.auth?.activeMembership?.organisationId,
        callerData_callerEmail: callerData?.caller_email,
        deviceIdentity,
        voicemailMode,
      });
      const numberOfCalls = requestedCount;
      const dialableProspects = filteredProspectQueue.filter(p => canCall(
        p,
        latestCallByPhone[p.prospect_phone],
        {
          dialedThisSession: dialedPhonesThisSessionRef.current,
          isAtCallLimit: isProspectAtCallLimit,
          selectedSource,
          scopeIds: dialScopeIds,
        },
      ));
      console.log('[DIAL] after canCall filter', {
        dialableCount: dialableProspects.length,
        sample: dialableProspects.slice(0, 3).map(p => ({
          name: p.prospect_name, phone: p.prospect_phone, status: p.status, source: p.source,
          intent: dialIntent(p, latestCallByPhone[p.prospect_phone]),
        })),
      });
      // attemptsByPhone : nb de calls historiques par phone, pour trier les
      // no_answer du moins-tente au plus-tente (equite : redonner une chance
      // a tout le monde avant d'insister sur les memes prospects).
      const attemptsByPhone = {};
      for (const c of (allCalls || [])) {
        const k = c.prospect_phone;
        if (!k || isInterruptedCall(c)) continue;
        attemptsByPhone[k] = (attemptsByPhone[k] || 0) + 1;
      }
      let dialableProspects2 = sortByDialIntent(dialableProspects, latestCallByPhone, attemptsByPhone);
      // Always ordered (the Smart Queue toggle is gone from the interface, and
      // without it the card's priority never reached Power Mode).
      {
        dialableProspects2 = [...dialableProspects2].sort((a, b) => {
          const intentA = dialIntent(a, latestCallByPhone[a.prospect_phone]);
          const intentB = dialIntent(b, latestCallByPhone[b.prospect_phone]);
          const tierOrder = { fresh: 0, no_answer: 1, contacted: 2 };
          const tierA = tierOrder[intentA] ?? 99;
          const tierB = tierOrder[intentB] ?? 99;
          if (tierA !== tierB) return tierA - tierB;
          // A intent egal no_answer : moins de tentatives d'abord.
          if (intentA === 'no_answer' && intentB === 'no_answer') {
            const attA = attemptsByPhone[a.prospect_phone] || 0;
            const attB = attemptsByPhone[b.prospect_phone] || 0;
            if (attA !== attB) return attA - attB;
          }
          return (b.priority || 0) - (a.priority || 0);
        });
      }
      const prospectsToCall = dialableProspects2.slice(0, numberOfCalls);
      console.log('[DIAL] prospectsToCall', {
        count: prospectsToCall.length,
        numberOfCalls,
      });
      if (prospectsToCall.length === 0) {
        const matchSource = (p) => selectedSource === 'Revcall Client Dashboard'
          ? (!p.source || p.source === selectedSource)
          : p.source === selectedSource;
        const inSource = prospectQueue.filter(matchSource);
        const totalInSource = inSource.length;
        const reasons = {
          noPhone: 0, invalidPhone: 0, doNotCall: 0, placeholder: 0,
          alreadyDialedThisSession: 0, atCallLimit: 0,
          archived: 0, callback: 0,
        };
        for (const p of inSource) {
          if (!p.prospect_phone?.trim()) { reasons.noPhone++; continue; }
          const digits = String(p.prospect_phone).trim().replace(/[^\d+]/g, '').replace(/\+/g, '');
          if (digits.length < 8 || digits.length > 15) { reasons.invalidPhone++; continue; }
          if (p.status === 'do_not_call' || p.do_not_call === true) { reasons.doNotCall++; continue; }
          if (p.status === 'placeholder') { reasons.placeholder++; continue; }
          if (dialedPhonesThisSessionRef.current.has(p.prospect_phone)) { reasons.alreadyDialedThisSession++; continue; }
          if (isProspectAtCallLimit(p.prospect_phone)) { reasons.atCallLimit++; continue; }
          const lastOutcome = latestCallByPhone[p.prospect_phone]?.call_outcome;
          if (hasMeetingBooked(latestCallByPhone[p.prospect_phone])) { reasons.archived++; continue; }
          if (lastOutcome && ARCHIVED_OUTCOMES.includes(lastOutcome)) { reasons.archived++; continue; }
          if (lastOutcome && CALLBACK_OUTCOMES.includes(lastOutcome)) { reasons.callback++; continue; }
        }
        console.log('[DIAL] no dialable prospects — detailed reasons', {
          totalInSource, reasons,
          allSources: [...new Set(prospectQueue.map(p => p.source))],
          allStatuses: [...new Set(prospectQueue.map(p => p.status))],
          dialedThisSession: Array.from(dialedPhonesThisSessionRef.current),
          maxCallAttempts, maxCallAttemptsPeriod,
        });
        if (totalInSource === 0) throw new Error('Aucun prospect dans cette liste.');
        const sorted = Object.entries(reasons).sort((a, b) => b[1] - a[1]);
        const [topReason, topCount] = sorted[0];
        if (topCount === 0) {
          throw new Error('Aucun prospect dialable trouvé pour cette liste. Vérifiez la source sélectionnée et la queue.');
        }
        const messages = {
          noPhone: t('dialer:session.noDialableReasons.noPhone'),
          invalidPhone: t('dialer:session.noDialableReasons.invalidPhone', { topCount, totalInSource }),
          doNotCall: t('dialer:session.noDialableReasons.doNotCall'),
          placeholder: t('dialer:session.noDialableReasons.placeholder'),
          alreadyDialedThisSession: t('dialer:session.noDialableReasons.alreadyDialedThisSession'),
          atCallLimit: t('dialer:session.noDialableReasons.atCallLimit', { maxCallAttempts, maxCallAttemptsPeriod }),
          archived: t('dialer:session.noDialableReasons.archived', { topCount }),
          callback: t('dialer:session.noDialableReasons.callback', { topCount }),
        };
        throw new Error(messages[topReason] || `Aucun prospect dialable. Détails : ${JSON.stringify(reasons)}`);
      }

      // The identity the live device holds (set by the device setup), the
      // React state only as a fallback: the conference room is joined under
      // the device's identity, and the server finds it by that name.
      const agentIdentity: string | null = (window as any).__twilioDeviceIdentity || deviceIdentity;
      if (!agentIdentity) throw new Error('Twilio device identity missing — please refresh the page');
      // Number rotation applies to Power Mode too: one outgoing number per
      // batch, the next one in the rotation (the selected one when fixed).
      const usedCallerId = (getNextCallerId ? getNextCallerId() : selectedCallerId) || '';
      // Marque les phones comme "dialés dans cette session" AVANT de créer les
      // ParallelCall — sinon l'auto-chain entre-temps peut re-trouver le même
      // prospect et lancer un second parallelDial (double-dial observé en prod).
      for (const p of prospectsToCall) {
        if (p.prospect_phone) {
          dialedPhonesThisSessionRef.current.add(p.prospect_phone);
        }
      }
      setDialedPhonesThisSession(Array.from(dialedPhonesThisSessionRef.current));
      console.log('[DIAL] creating ParallelCall records', { count: prospectsToCall.length });
      // 2026-05-15 — fix bug "Contact" : construit un nom non-vide pour eviter
      // que Twilio customParameters envoie prospectName="" (cas Qing Sun).
      // Fallback en cascade : prospect_name → first+last_name → company → phone.
      const resolveName = (p) => {
        const trim = (s) => (s && String(s).trim()) || '';
        if (trim(p.prospect_name)) return trim(p.prospect_name);
        const first = trim(p.first_name);
        const last = trim(p.last_name);
        if (first || last) return `${first} ${last}`.trim();
        if (trim(p.full_name)) return trim(p.full_name);
        if (trim(p.prospect_company)) return trim(p.prospect_company);
        if (trim(p.prospect_phone)) return trim(p.prospect_phone);
        return 'Prospect';
      };
      let callRecords;
      try {
        callRecords = await Promise.all(prospectsToCall.map(async (prospect) => {
          const resolvedName = resolveName(prospect);
          if (resolvedName !== prospect.prospect_name) {
            console.warn('[DIAL] prospect_name vide ou manquant → fallback', { phone: prospect.prospect_phone, resolved: resolvedName, original: prospect.prospect_name });
          }
          console.log('[DIAL] ParallelCall.create for', prospect.prospect_phone);
          const call = await api.entities.ParallelCall.create({ prospect_name: resolvedName, prospect_phone: prospect.prospect_phone, prospect_company: prospect.prospect_company, customer_id: prospect.customer_id || '', call_status: 'dialing', agent_email: callerData?.caller_email, agent_name: callerData?.caller_name, from_number: usedCallerId.replace(/\s+/g, ''), started_at: new Date().toISOString() });
          console.log('[DIAL] ParallelCall.create OK', { callId: call.id, phone: prospect.prospect_phone });
          return { id: prospect.id, queueId: prospect.id, callId: call.id, phone: prospect.prospect_phone, name: resolvedName };
        }));
      } catch (err) {
        console.error('[DIAL] ParallelCall.create / ProspectQueue.update FAILED', err, {
          status: err?.status, code: err?.error?.code, message: err?.message,
        });
        throw err;
      }
      console.log('[DIAL] all ParallelCall records created', { count: callRecords.length });

      if (completeTaskOnDial) {
        const dialedPhones = new Set(prospectsToCall.map(p => p.prospect_phone?.replace(/\D/g, '')));
        const pendingFollowUps = allCalls.filter(c => c.call_outcome === 'callback_requested' && c.follow_up_at && !c.follow_up_completed && dialedPhones.has(c.prospect_phone?.replace(/\D/g, '')));
        for (const fu of pendingFollowUps) api.entities.ParallelCall.update(fu.id, { follow_up_completed: true }).catch(() => {});
      }

      console.log('[DIAL] invoking parallelDialProspectsPublic', {
        prospects: callRecords.length,
        agentIdentity,
        fromNumber: usedCallerId.replace(/\s+/g, ''),
        voicemailMode,
        hasVoicemailUrl: !!voicemailAudioUrl,
      });
      // Rollback helper : si /twilio/parallel-dial fail, on revert les optimistic
      // updates sinon des "appels fantomes" restent visibles.
      const rollbackOptimisticUpdates = async (reason) => {
        console.warn('[DIAL] rolling back optimistic updates', { reason, count: callRecords.length });
        const nowIso = new Date().toISOString();
        await Promise.allSettled(callRecords.map(async (cr) => {
          try {
            await api.entities.ParallelCall.update(cr.callId, {
              call_status: 'failed',
              call_outcome: 'not_reachable',
              ended_at: nowIso,
              // i18n-exempt: call note stored on the call record, not interface copy
              call_notes: `Echec lancement: ${reason}`,
            });
          } catch (e) {
            console.error('[DIAL] rollback ParallelCall.update failed', cr.callId, e);
          }
          if (cr.phone) dialedPhonesThisSessionRef.current.delete(cr.phone);
        }));
        setDialedPhonesThisSession(Array.from(dialedPhonesThisSessionRef.current));
        queryClient.invalidateQueries({ queryKey: ['prospect-queue'] });
        queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
      };

      let data;
      try {
        // Standing conference (2026-10-06): joins it if it is switched on for
        // this caller and not up yet. False = classic flow, nothing lost.
        try { await window.__revcallRoom?.ensure?.(); } catch (_e) { /* classic flow */ }
        const resp = await TwilioCalls.parallelDial({ prospects: callRecords, agentIdentity, fromNumber: usedCallerId.replace(/\s+/g, ''), voicemailMode, voicemailAudioUrl, liveCoachEnabled: aiCoachEnabledPowerMode });
        data = resp.data;
        console.log('[DIAL] parallelDialProspectsPublic response', { success: data?.success, callsReturned: data?.calls?.length });
      } catch (err) {
        console.error('[DIAL] parallelDialProspectsPublic FAILED', err, {
          status: err?.status, code: err?.error?.code, message: err?.message,
        });
        await rollbackOptimisticUpdates(err?.error?.code || err?.message || 'twilio_dial_failed');
        throw err;
      }
      if (!data.success) {
        console.error('[DIAL] backend returned success=false', data);
        await rollbackOptimisticUpdates('backend_returned_success_false');
        throw new Error('Echec du lancement des appels');
      }
      await Promise.all(data.calls.filter(c => c.success).map(call => api.entities.ParallelCall.update(call.callId, { external_call_id: call.callSid, call_status: 'ringing' })));
      console.log('[DIAL] all ringing, mutation success');
      return data;
    },
    onSuccess: (data) => {
      console.log('[DIAL] onSuccess', { calls: data?.calls?.length, stopRequested: stopRequestedRef.current, modalOpen: !!connectedContactRef.current });
      // Si la modale est ouverte (appel actif ou qualification), hangup les
      // nouveaux calls pour ne pas interrompre l'agent.
      if (connectedContactRef.current) {
        console.warn('[DIAL] onSuccess but in qualification → hangup new calls');
        const nowIso = new Date().toISOString();
        const newCalls = (data?.calls || []).filter(c => c.success);
        void Promise.all(newCalls.map(async (c) => {
          try { if (c.callSid) await TwilioCalls.hangupCall(c.callSid); } catch (_e) {}
          // i18n-exempt: call note stored on the call record, not interface copy
          try { await api.entities.ParallelCall.update(c.callId, { call_status: 'failed', call_outcome: 'not_reachable', call_notes: 'Arret automatique (qualification en cours)', ended_at: nowIso }); } catch (_e) {}
        })).then(() => {
          queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
        });
        return;
      }
      const sinceStop = Date.now() - lastStopAtRef.current;
      if (stopRequestedRef.current || sinceStop < 3000) {
        console.warn(`[DIAL] onSuccess but stop active (stopRequested=${stopRequestedRef.current} sinceStopMs=${sinceStop}) → hangup new calls`);
        const nowIso = new Date().toISOString();
        const newCalls = (data?.calls || []).filter(c => c.success);
        void Promise.all(newCalls.map(async (c) => {
          try { if (c.callSid) await TwilioCalls.hangupCall(c.callSid); } catch (_e) {}
          try { await api.entities.ParallelCall.update(c.callId, { call_status: 'failed', call_outcome: 'not_reachable', call_notes: 'Arret manuel (race Stop/Start)', ended_at: nowIso }); } catch (_e) {}
        })).then(() => {
          queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
        });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['prospect-queue'] });
      queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
      // Toast affiche SEULEMENT au 1er dial manuel (transition idle → running).
      // Sur les refills auto suivants, silence radio.
      if (dialerRunState !== 'running') {
        toast.success(t('dialer:session.callsLaunched'));
      }
      setDialerRunState('running');
      if (powerHourActive && data?.calls) {
        setPowerHourCalls(prev => prev + data.calls.filter(c => c.success).length);
      }
    },
    onError: (error) => {
      console.error('[DIAL] onError', error, { message: error?.message, status: error?.status, code: error?.error?.code });
      setDialerRunState('idle');
      setPowerMode(false);
      const code = error?.error?.code || error?.code;
      let msg = error?.message || 'Impossible de lancer les appels';
      if (code === 'NO_TENANT_NUMBER') {
        msg = "Aucun numéro Twilio provisionné. Allez dans Paramètres > Numéros pour en obtenir un.";
      } else if (code === 'DAILY_CAP_EXCEEDED') {
        msg = "Limite quotidienne d'appels atteinte. Réessayez demain ou ajustez votre forfait.";
      } else if (['TRIAL_EXPIRED', 'SUBSCRIPTION_SUSPENDED', 'NO_SUBSCRIPTION'].includes(code)) {
        msg = "Abonnement requis pour lancer des appels. Allez dans Facturation.";
      } else if (code === 'AUTH_REQUIRED' || error?.status === 401) {
        msg = "Session expirée — reconnectez-vous.";
      } else if (code === 'DEVICE_NOT_READY') {
        // T-d1: starting a session is the one place the telephony modal
        // blocks; it explains the cause with its code.
        window.dispatchEvent(new CustomEvent(TELEPHONY_REQUIRED_EVENT));
        return;
      }
      toast.error(msg);
    },
    onSettled: (_data, _err, input) => {
      const requested = Math.max(1, Math.min(
        parallelCallsCount,
        Number.isFinite(input?.count) ? input.count : parallelCallsCount,
      ));
      pendingDialsRef.current = Math.max(0, pendingDialsRef.current - requested);
      console.log('[DIAL] onSettled: pendingDialsRef -', requested, '→', pendingDialsRef.current);
    },
  });
}


/**
 * Hook agrégat — appelle useDialingMutation puis définit les 5 handlers
 * (handleStartDialing/ManualStartDialing/StopAll/HangupAndContinue/
 * HangupSingleCall/SkipBatch). Toutes les déps sont injectées via args.
 */
export function useDialerSessionHandlers(args) {
  const { t } = useTranslation(['dialer']);
  const {
    pendingDialsRef, stopRequestedRef, lastStopAtRef,
    connectedContactRef, dialedPhonesThisSessionRef,
    autoDialTimerRef, skipQualificationRef,
    prospectQueue, activeCalls, dialerRunState, stoppingAll, powerMode,
    callerData, selectedCustomerId,
    setDialedPhonesThisSession, setDialerRunState, setStoppingAll,
    setPowerMode, setShowCallSheet, setConnectedContact, setSelectedProspect,
    setPowerHourActive, setPowerHourTimer, setPowerHourCalls,
    powerHourActive,
    queryClient,
    startDialingMutation,
    restartDialingSessionClock,
  } = args;

  const handleStartDialing = useCallback((opts = {}) => {
    const manual = opts?.manual === true;
    console.log('[DIAL] handleStartDialing entry', {
      manual,
      isPending: startDialingMutation.isPending,
      dialerRunState,
      prospectQueueSize: prospectQueue?.length,
    });
    if (startDialingMutation.isPending) {
      console.warn('[DIAL] aborted — mutation already pending');
      return;
    }
    if (!manual && stopRequestedRef.current) {
      console.warn('[DIAL] aborted auto-chain — stopRequestedRef=true');
      return;
    }
    if (!manual && (dialerRunState !== 'running' || stoppingAll)) {
      console.warn('[DIAL] aborted auto-chain — Stop a ete clique entre-temps', { dialerRunState, stoppingAll });
      return;
    }
    if (manual) {
      stopRequestedRef.current = false;
    }
    try { window.dataLayer && window.dataLayer.push({ event: 'StartDial' }); } catch (_e) {}
    if (manual) {
      // A manual Lancer starts a new session: the bar's timer restarts too (RUI-08).
      restartDialingSessionClock?.();
      dialedPhonesThisSessionRef.current.clear();
      setDialedPhonesThisSession([]);
    } else if (dialerRunState === 'idle') {
      dialedPhonesThisSessionRef.current.clear();
      setDialedPhonesThisSession([]);
    }
    setPowerMode(true);
    console.log('[DIAL] invoking startDialingMutation.mutate()');
    startDialingMutation.mutate();
  }, [startDialingMutation, dialerRunState, prospectQueue, stoppingAll,
      stopRequestedRef, dialedPhonesThisSessionRef, setDialedPhonesThisSession, setPowerMode, restartDialingSessionClock]);

  const handleManualStartDialing = useCallback(() => {
    handleStartDialing({ manual: true });
  }, [handleStartDialing]);

  // W3 call page (ENG-2): « Enregistrer et continuer » resumes the session
  // that the hang-up paused. It is not a new session: the clock and the
  // numbers already dialed are kept (handleManualStartDialing resets both).
  // It calls the mutation itself, because handleStartDialing's auto guard
  // reads the dialerRunState of its render, still 'paused' at this point.
  // Returns false when a dial is still in flight: the caller keeps its banner.
  const handleResumeDialing = useCallback((): boolean => {
    if (startDialingMutation.isPending) return false;
    stopRequestedRef.current = false;
    if (autoDialTimerRef.current) {
      clearTimeout(autoDialTimerRef.current);
      autoDialTimerRef.current = null;
    }
    setPowerMode(true);
    setDialerRunState('running');
    startDialingMutation.mutate();
    return true;
  }, [startDialingMutation, stopRequestedRef, autoDialTimerRef, setPowerMode, setDialerRunState]);

  // Hang up and finalise the given calls. Never overwrites notes or an
  // outcome already set (RUI-P1/P1b, stopUpdatesFor). Failures are collected
  // and shown with a retry instead of being swallowed (CEO-13): two retries,
  // then the caller is told to hang up from the line.
  const finalizeStoppedCalls = useCallback(async (calls, defaultNote, attempt = 0) => {
    const nowIso = new Date().toISOString();
    const failed = [];
    await Promise.all((calls || []).map(async (call) => {
      try { if (call.external_call_id) await TwilioCalls.hangupCall(call.external_call_id); }
      catch (e) { console.warn('[DIAL] Stop: twilioHangupCall failed for', call.id, e?.message); }
      try { await api.entities.ParallelCall.update(call.id, stopUpdatesFor(call, nowIso, defaultNote)); }
      catch (e) {
        console.warn('[DIAL] Stop: ParallelCall.update failed for', call.id, e?.message);
        failed.push(call);
      }
    }));
    queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
    if (failed.length > 0) {
      if (attempt < 2) {
        toast.error(t('dialer:session.stopFailed', { count: failed.length }), {
          action: { label: t('dialer:session.retry'), onClick: () => { void finalizeStoppedCalls(failed, defaultNote, attempt + 1); } },
        });
      } else {
        toast.error(t('dialer:session.stopFailedFinal', { ids: failed.map((c) => c.id).join(', ') }));
      }
    }
    return failed;
  }, [queryClient, t]);

  // "Arrêter la numérotation" (ENG-1): stops new dials and ends the lines
  // still ringing, but keeps the audible conversation and the conference
  // leg. Ending the conversation is a separate action (Raccrocher).
  const handleStopDialing = useCallback(() => {
    const calls = activeCalls || [];
    stopRequestedRef.current = true;
    lastStopAtRef.current = Date.now();
    pendingDialsRef.current = 0;
    if (autoDialTimerRef.current) {
      clearTimeout(autoDialTimerRef.current);
      autoDialTimerRef.current = null;
    }
    setPowerMode(false);
    setDialerRunState('paused');
    const silentLines = calls.filter((c) => !isAudibleCall(c));
    if (silentLines.length > 0) void finalizeStoppedCalls(silentLines, 'Arret manuel');
    toast.success(t('dialer:session.dialingStopped'));
  }, [activeCalls, autoDialTimerRef, stopRequestedRef, lastStopAtRef, pendingDialsRef,
      setPowerMode, setDialerRunState, finalizeStoppedCalls, t]);

  const handleStopAll = useCallback(() => {
    const callsToHangup = activeCalls || [];
    console.log('[DIAL] handleStopAll called', { activeCallsCount: callsToHangup.length, powerMode, dialerRunState, hasArmedTimer: !!autoDialTimerRef.current });

    stopRequestedRef.current = true;
    lastStopAtRef.current = Date.now();
    pendingDialsRef.current = 0;

    if (autoDialTimerRef.current) {
      clearTimeout(autoDialTimerRef.current);
      autoDialTimerRef.current = null;
      console.log('[DIAL] handleStopAll: cleared armed auto-dial timer');
    }
    setPowerMode(false);
    setDialerRunState('paused');
    setStoppingAll(true);

    try {
      // End of session: leave the standing conference too (no-op when off).
      try { window.__revcallRoom?.leave?.(); } catch (_e) {}
      disconnectDeviceCalls(window.__twilioDevice);
    } catch (e) { console.warn('[DIAL] Stop: device.disconnect failed', e?.message); }

    // 2026-06-03 — Filet de securite serveur. Le device.disconnect() coupe
    // uniquement la leg Client cote browser. Pour un appel SIP inbound
    // (Zadarma -> Twilio -> Client), Twilio peut basculer la leg parent
    // sur voicemail-fallback qui joue Polly.Celine au prospect au lieu
    // de raccrocher. /twilio/hangup-all-mine raccroche TOUS les CallSid
    // actifs de l'agent cote Twilio API. Idempotent.
    TwilioCalls.hangupAllMine();

    // Reset l'etat "appel en cours" cote UI. Sans ca, le bandeau bas, la
    // CallSheet et la modale ProspectDetailPanel restent affiches comme si
    // l'appel etait toujours en cours apres clic sur Raccrocher.
    // 2026-05-29 + 2026-06-02 (Kevin audit P1.1) : setSelectedProspect(null)
    // ajoute, sans ca la modale ProspectDetailPanel reste ouverte indefiniment.
    try {
      setConnectedContact(null);
      setShowCallSheet(false);
      setSelectedProspect && setSelectedProspect(null);
    } catch (e) { console.warn('[DIAL] Stop: cleanup UI failed', e?.message); }

    queryClient.setQueryData(
      ['parallel-calls', callerData?.id, selectedCustomerId, callerData?.caller_email],
      []
    );
    toast.success(t('dialer:session.callsStopped'));
    setStoppingAll(false);

    void finalizeStoppedCalls(callsToHangup, 'Arret manuel');
  }, [activeCalls, queryClient, powerMode, dialerRunState,
      autoDialTimerRef, stopRequestedRef, lastStopAtRef, pendingDialsRef,
      callerData, selectedCustomerId, finalizeStoppedCalls,
      setPowerMode, setDialerRunState, setStoppingAll,
      setConnectedContact, setShowCallSheet, setSelectedProspect]);

  const handleHangupAndContinue = useCallback(async () => {
    skipQualificationRef.current = true;
    // HANGUP-RACE-1: stop the refill before anything else. Clearing the call
    // on screen while the session is still 'running' let the rolling refill
    // dial a new series during the network calls below, which the stop then
    // hung up (ghost calls counted as not_reachable attempts).
    stopRequestedRef.current = true;
    if (autoDialTimerRef.current) { clearTimeout(autoDialTimerRef.current); autoDialTimerRef.current = null; }
    setPowerMode(false);
    setDialerRunState('paused');
    setShowCallSheet(false);
    setConnectedContact(null);
    setSelectedProspect && setSelectedProspect(null);

    setStoppingAll(true);
    const nowIso = new Date().toISOString();
    try {
      disconnectDeviceCalls(window.__twilioDevice, [window.__revcallRoom?.roomCall?.()]); // the standing conference stays for the next prospect
      // Filet de securite serveur (cf. handleStopAll) : hangup TOUS les
      // CallSid actifs de l'agent cote Twilio. Sans ca un inbound SIP
      // peut basculer sur voicemail-fallback au lieu de raccrocher.
      TwilioCalls.hangupAllMine();
      await Promise.all((activeCalls || []).map(async (call) => {
        try { if (call.external_call_id) await TwilioCalls.hangupCall(call.external_call_id); } catch (_e) {}
        try { await api.entities.ParallelCall.update(call.id, stopUpdatesFor(call, nowIso, 'Raccrocher et continuer')); } catch (_e) {}
      }));
      // 2026-05-21 — Plus de reprise AUTO du dial apres avoir raccroche avec
      // un humain. L'agent doit cliquer explicitement "Continuer les appels" :
      // la session est en 'paused', sans timer de relance (pose au debut,
      // HANGUP-RACE-1). 2026-06-02 (audit P1.4) : powerMode a false aussi,
      // sinon l'etat reste "mixte" (paused + powerMode=true).
      queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
      skipQualificationRef.current = false;
    } catch (_e) {
      toast.error(t('dialer:session.hangupError'));
      skipQualificationRef.current = false;
    } finally {
      setStoppingAll(false);
    }
  }, [activeCalls, queryClient,
      skipQualificationRef, stopRequestedRef, autoDialTimerRef,
      setShowCallSheet, setConnectedContact, setSelectedProspect,
      setStoppingAll, setDialerRunState, setPowerMode]);

  // 2026-05-21 — "Raccrocher et passer au suivant" : raccroche l'appel humain
  // puis RELANCE explicitement le power mode. C'est le clic explicite que le
  // Lot 1a impose (pas de reprise auto sans action) : ici l'agent demande
  // expressement d'enchainer, donc on relance le dial immediatement.
  const handleHangupAndNext = useCallback(async () => {
    skipQualificationRef.current = true;
    setShowCallSheet(false);
    setConnectedContact(null);
    setSelectedProspect && setSelectedProspect(null);
    setStoppingAll(true);
    const nowIso = new Date().toISOString();
    try {
      disconnectDeviceCalls(window.__twilioDevice, [window.__revcallRoom?.roomCall?.()]); // the standing conference stays for the next prospect
      // Filet de securite serveur (idem handleStopAll / handleHangupAndContinue).
      TwilioCalls.hangupAllMine();
      await Promise.all((activeCalls || []).map(async (call) => {
        try { if (call.external_call_id) await TwilioCalls.hangupCall(call.external_call_id); } catch (_e) {}
        const isVoicemail = call.call_status === 'voicemail' || call.call_outcome === 'voicemail' || call.call_outcome === 'left_voicemail';
        const hasHumanSignal = !isVoicemail && (['connected_to_agent', 'human_detected'].includes(call.call_status) || !!call.connected_at);
        let fallbackOutcome;
        if (isVoicemail) fallbackOutcome = 'no_answer';
        else if (hasHumanSignal) fallbackOutcome = null;
        else fallbackOutcome = 'not_reachable';
        const outcomeToWrite = call.call_outcome || fallbackOutcome;
        const updates = { call_status: 'completed', call_notes: call.call_notes || 'Raccrocher et passer au suivant', ended_at: nowIso };
        if (outcomeToWrite) updates.call_outcome = outcomeToWrite;
        try { await api.entities.ParallelCall.update(call.id, updates); } catch (_e) {}
      }));
      // Relance explicite : on REPART en 'running' et on arme la relance.
      stopRequestedRef.current = false;
      setPowerMode(true);
      setDialerRunState('running');
      queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
      if (autoDialTimerRef.current) clearTimeout(autoDialTimerRef.current);
      autoDialTimerRef.current = setTimeout(() => {
        autoDialTimerRef.current = null;
        if (stopRequestedRef.current) return;
        handleStartDialing();
        setTimeout(() => { skipQualificationRef.current = false; }, 3000);
      }, 600);
    } catch (_e) {
      toast.error(t('dialer:session.hangupError'));
      skipQualificationRef.current = false;
    } finally {
      setStoppingAll(false);
    }
  }, [activeCalls, queryClient, handleStartDialing,
      skipQualificationRef, stopRequestedRef, autoDialTimerRef,
      setShowCallSheet, setConnectedContact, setSelectedProspect,
      setStoppingAll, setDialerRunState, setPowerMode]);

  const handleHangupSingleCall = useCallback(async (callId) => {
    const call = activeCalls.find(c => c.id === callId);
    if (!call) return;
    const nowIso = new Date().toISOString();
    try { if (call.external_call_id) await TwilioCalls.hangupCall(call.external_call_id); } catch (_e) {}
    // i18n-exempt: call note stored on the call record, not interface copy
    try { await api.entities.ParallelCall.update(call.id, { call_status: 'completed', call_outcome: 'not_reachable', call_notes: 'Raccroche manuellement depuis la liste', ended_at: nowIso }); } catch (_e) {}
    queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
    queryClient.invalidateQueries({ queryKey: ['all-calls'] });
  }, [activeCalls, queryClient]);

  const handleSkipBatch = useCallback(async () => {
    if (dialerRunState !== 'running') return;
    const nonConnected = activeCalls.filter(c => ['dialing', 'ringing'].includes(c.call_status));
    if (nonConnected.length === 0) return;
    const nowIso = new Date().toISOString();
    await Promise.all(nonConnected.map(async (call) => {
      try { if (call.external_call_id) await TwilioCalls.hangupCall(call.external_call_id); } catch (_e) {}
      try { await api.entities.ParallelCall.update(call.id, { call_status: 'completed', call_outcome: 'not_reachable', call_notes: 'Batch skipe', ended_at: nowIso }); } catch (_e) {}
    }));
    queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
    toast.info(t('dialer:session.batchSkipped'));
    if (autoDialTimerRef.current) clearTimeout(autoDialTimerRef.current);
    autoDialTimerRef.current = setTimeout(() => {
      autoDialTimerRef.current = null;
      if (stopRequestedRef.current) { console.log('[SkipBatch] timer fired but stopRequestedRef=true → skip'); return; }
      if (dialerRunState !== 'running') { console.log('[SkipBatch] timer fired but dialerRunState=', dialerRunState, '→ skip'); return; }
      handleStartDialing();
    }, 500);
  }, [activeCalls, dialerRunState, handleStartDialing, queryClient,
      autoDialTimerRef, stopRequestedRef]);

  // Correction #3 (revue client XDIAL) — la Power Hour est un CHRONO, rien
  // d'autre. Deux choses lançaient des appels sur un simple clic :
  //   1. le bouton du dialer enchaînait handleManualStartDialing() (supprimé
  //      avec PowerHourBadge.tsx, cf. PowerHourPanel.tsx) ;
  //   2. ici même, setPowerMode(true) forçait l'auto-chaînage — sans effet
  //      dialer à l'arrêt, mais sur une session déjà lancée en mode manuel,
  //      démarrer la Power Hour faisait repartir les appels tout seuls.
  // Les deux sont retirés. Le mode d'appel reste au choix de l'agent.
  const handleTogglePowerHour = useCallback((durationMinutes = 60) => {
    if (powerHourActive) {
      setPowerHourActive(false);
      setPowerHourTimer(Math.max(1, durationMinutes) * 60);
      toast.info(t('dialer:session.powerHourStopped'));
    } else {
      const seconds = Math.max(1, durationMinutes) * 60;
      setPowerHourActive(true);
      setPowerHourTimer(seconds);
      setPowerHourCalls(0);
      toast.success(t('dialer:session.powerHourStarted', { minutes: durationMinutes }));
    }
  }, [powerHourActive, setPowerHourActive, setPowerHourTimer, setPowerHourCalls]);

  return {
    handleStartDialing, handleManualStartDialing, handleResumeDialing,
    handleStopAll, handleStopDialing, handleHangupAndContinue, handleHangupAndNext, handleHangupSingleCall,
    handleSkipBatch, handleTogglePowerHour,
  };
}


/**
 * useRollingRefill — Phase 4 etape 7b-3.
 *
 * Modele "rolling" du Power Mode : maintient N=parallelCallsCount calls
 * actifs en parallèle. Dès qu'un slot se libère (un call passe en terminal
 * et sort de activeCalls), on relance un dial pour combler le creux.
 *
 * Bug du modèle "wave" précédent (fix 2026-05-13) : si tous les calls
 * partaient en 'no_answer'/'failed' instantanément, ils ne passaient jamais
 * dans activeCalls (filtre 'dialing'/'ringing'/...), donc la transition
 * prevCount=0→currentCount>0 ne survenait jamais et le verrou se figeait.
 * Rolling refill : pas de verrou de transition, juste un slotsToFill
 * calculé à chaque render.
 *
 * Notes sur les refs :
 * - stopRequestedRef : flag synchrone anti-race STOP_ALL
 * - lastDialDispatchAtRef : verrou anti-burst (1500ms cooldown)
 * - singleLineEnforceRef : verrou anti-rentrant single-line enforce
 * - autoDialTimerRef : timer du dispatch
 * - pendingDialsRef : compteur mutations en vol (anti-burst "4 au lieu de 3")
 */
export function useRollingRefill({
  // Refs
  pendingDialsRef, stopRequestedRef, autoDialTimerRef,
  lastDialDispatchAtRef, dialedPhonesThisSessionRef, singleLineEnforceRef,
  connectedContactRef,
  // States
  activeCalls, parallelCallsCount, dialerRunState, stoppingAll, powerMode,
  powerModePauseBetweenBatches, prospectQueue, latestCallByPhone,
  isProspectAtCallLimit, selectedSource, showCallSheet, connectedContact,
  // Setters
  setDialerRunState, setPowerMode,
  // Mutation
  startDialingMutation,
  dialScopeIds = null,
}) {
  const { t } = useTranslation(['dialer']);
  useEffect(() => {
    const currentCount = activeCalls.length + pendingDialsRef.current;
    const slotsToFill = Math.max(0, (parallelCallsCount || 1) - currentCount);

    if (stopRequestedRef.current) {
      console.log('[AutoChain] bail: stopRequestedRef=true');
      return;
    }
    if (dialerRunState !== 'running') {
      return;
    }
    if (stoppingAll) {
      console.log('[AutoChain] bail: stoppingAll=true');
      return;
    }
    if (!powerMode) {
      if (currentCount === 0) {
        console.log('[AutoChain] manual mode end → setDialerRunState idle');
        setDialerRunState('idle');
      }
      return;
    }
    if (startDialingMutation.isPending) return;
    const sinceDispatch = Date.now() - lastDialDispatchAtRef.current;
    if (sinceDispatch < 1500) {
      console.log('[AutoChain] bail: dispatch cooldown', { sinceDispatch });
      return;
    }
    if (singleLineEnforceRef.current) {
      console.log('[AutoChain] bail: singleLineEnforceRef=true (humain pris)');
      return;
    }
    const humanConnected = activeCalls.some(c => c.call_status === 'connected_to_agent' || c.call_status === 'human_detected');
    if (humanConnected) {
      console.log('[AutoChain] bail: human connected, freeze refill');
      return;
    }
    if (showCallSheet && connectedContact) {
      console.log('[AutoChain] bail: modale ouverte (connectedContact), Power Mode gele');
      return;
    }
    if (slotsToFill === 0) return;

    console.log('[AutoChain] evaluation', {
      currentCount, parallelCallsCount, slotsToFill,
      stopRequested: stopRequestedRef.current,
      dialerRunState, powerMode, stoppingAll,
      isPending: startDialingMutation.isPending,
    });

    const nextProspect = prospectQueue.find(p => canCall(
      p,
      latestCallByPhone[p.prospect_phone],
      {
        dialedThisSession: dialedPhonesThisSessionRef.current,
        isAtCallLimit: isProspectAtCallLimit,
        selectedSource,
        scopeIds: dialScopeIds,
      },
    ));
    if (!nextProspect) {
      setDialerRunState('idle');
      if (powerMode) setPowerMode(false);
      toast.info(t('dialer:session.allProspectsCalled'));
      return;
    }

    const techDelay = 500;
    const userDelay = Math.max(0, (powerModePauseBetweenBatches || 0) * 1000);
    const totalDelay = techDelay + userDelay;
    if (autoDialTimerRef.current) clearTimeout(autoDialTimerRef.current);
    autoDialTimerRef.current = setTimeout(() => {
      autoDialTimerRef.current = null;
      if (stopRequestedRef.current) { console.log('[AutoChain] timer fired but stopRequestedRef=true → skip'); return; }
      if (dialerRunState !== 'running') { console.log('[AutoChain] timer fired but dialerRunState=', dialerRunState, '→ skip'); return; }
      if (singleLineEnforceRef.current) { console.log('[AutoChain] timer fired but singleLineEnforceRef=true → skip'); return; }
      // Bug Aoife Murphy 2026-05-15 : si une modale s'est ouverte entre
      // l'arming du timer et son fire (500ms+), on doit annuler le dial.
      // connectedContactRef est synchrone (mis a jour avant chaque render).
      if (connectedContactRef?.current) {
        console.log('[AutoChain] timer fired but modale ouverte (connectedContactRef) → skip');
        return;
      }
      lastDialDispatchAtRef.current = Date.now();
      startDialingMutation.mutate({ count: slotsToFill });
    }, totalDelay);
  }, [activeCalls, dialScopeIds, powerMode, powerModePauseBetweenBatches, prospectQueue,
      dialerRunState, parallelCallsCount, isProspectAtCallLimit, startDialingMutation,
      startDialingMutation.isPending, stoppingAll, latestCallByPhone, selectedSource,
      showCallSheet, connectedContact,
      pendingDialsRef, stopRequestedRef, autoDialTimerRef, lastDialDispatchAtRef,
      dialedPhonesThisSessionRef, singleLineEnforceRef,
      setDialerRunState, setPowerMode]);
}
